import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { platformApi } from '../../../api/endpoints/platform';
import { handleApiError } from '../../../api/errorMapping';
import { generateIdempotencyKey } from '../../../lib/idempotency';
import { Gate } from '../../../components/permissions/Gate';
import { Button } from '../../../components/ui/button';
import { useAdminAssignmentLabels } from '../../../hooks/useAdminAssignmentLabels';
import { AdminAssignmentsList } from './AdminAssignmentsList';
import {
    RoleScopePicker,
    buildAssigns,
    hasValidAssigns,
    emptyRoleScope,
    type RoleScopeValue,
} from './RoleScopePicker';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Admin platform cuyas asignaciones se gestionan. */
    cognitoSub: string;
    adminLabel: string;
}

/**
 * Gestión COMPLETA de roles de un ADMIN PLATFORM: ver actuales / agregar / quitar. Es el equivalente
 * platform de `ManageUserRolesDialog` (que sirve a tenant/retailer): unifica en una sola acción lo que
 * antes estaban separados en "Asignar rol" (AssignRoleDialog) + "Ver asignaciones".
 *
 *  - "Roles actuales": lista cada `role-assignment` (rol + alcance por nombre) con un botón
 *    "Quitar" (revoke) gateado por `platform.role.update`. Revoca por `assignmentId`.
 *  - "Agregar rol": reusa `RoleScopePicker` (toggle GLOBAL / por-tenant) — el selector propio de la
 *    entidad platform (NO el RoleScopeFields rol→scope→scopeRef del tenant). El backend RECALCULA el
 *    effective scope desde el rol (220f7b4): global → scopeRef '*'; por-tenant → un assign por tenant.
 *
 * Overlay inline (no Radix Dialog) — patrón anti-freeze del repo, igual que AssignRoleDialog.
 */
export function ManageAdminRolesDialog({ open, onOpenChange, cognitoSub, adminLabel }: Props) {
    const qc = useQueryClient();
    const [value, setValue] = useState<RoleScopeValue>(emptyRoleScope);
    // assignmentId en curso de revocación (para deshabilitar solo ese botón).
    const [revokingId, setRevokingId] = useState<string | null>(null);

    const assignmentsKey = ['platform', 'admins', cognitoSub, 'role-assignments'];

    const assignmentsQ = useQuery({
        queryKey: assignmentsKey,
        queryFn: () => platformApi.listAdminRoleAssignments(cognitoSub),
        enabled: open,
    });

    const invalidate = (): Promise<void> => qc.invalidateQueries({ queryKey: assignmentsKey });

    const assignMut = useMutation({
        mutationFn: async () => {
            // Un assign por cada `{ roleId, scopeRef }` (global → uno con '*'; por-tenant → uno por tenant).
            for (const body of buildAssigns(value)) {
                await platformApi.assignAdminRole(cognitoSub, body, {
                    idempotencyKey: generateIdempotencyKey(),
                });
            }
        },
        onSuccess: async () => {
            toast.success('Rol asignado');
            setValue(emptyRoleScope);
            await invalidate();
        },
        onError: handleApiError,
    });

    const revokeMut = useMutation({
        mutationFn: (assignmentId: string) => platformApi.revokeAdminRole(cognitoSub, assignmentId),
        onMutate: (assignmentId: string) => {
            setRevokingId(assignmentId);
        },
        onSuccess: async () => {
            toast.success('Rol quitado');
            await invalidate();
        },
        onError: handleApiError,
        onSettled: () => {
            setRevokingId(null);
        },
    });

    const labels = useAdminAssignmentLabels(open);

    if (!open) return null;

    const items = assignmentsQ.data?.items ?? [];
    const canSubmit = hasValidAssigns(value);

    return (
        <div
            role="dialog"
            aria-label="Gestionar roles"
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16"
        >
            <button
                type="button"
                aria-label="Cerrar"
                className="fixed inset-0 bg-black/50"
                onClick={() => {
                    onOpenChange(false);
                }}
            />
            <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                <div className="mb-1 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Gestionar roles</h2>
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    >
                        ✕
                    </button>
                </div>
                <p className="mb-4 text-sm text-muted-foreground">{adminLabel}</p>

                {/* ── Roles actuales ── */}
                <div className="space-y-2">
                    <p className="text-sm font-medium">Roles actuales</p>
                    <AdminAssignmentsList
                        items={items}
                        isPending={assignmentsQ.isPending}
                        isError={assignmentsQ.isError}
                        onRetry={() => {
                            void assignmentsQ.refetch();
                        }}
                        labels={labels}
                        renderAction={(a) => (
                            <Gate permission="platform.role.update" scope="PLATFORM">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    aria-label={`Quitar ${labels.roleName(a.roleId)}`}
                                    disabled={revokeMut.isPending && revokingId === a.assignmentId}
                                    onClick={() => {
                                        revokeMut.mutate(a.assignmentId);
                                    }}
                                >
                                    <Trash2 className="size-4" />
                                    <span className="ml-1">Quitar</span>
                                </Button>
                            </Gate>
                        )}
                    />
                </div>

                {/* ── Agregar rol ── */}
                <Gate permission="platform.role.update" scope="PLATFORM">
                    <div className="mt-6 border-t pt-4">
                        <p className="mb-3 text-sm font-medium">Agregar rol</p>
                        <RoleScopePicker
                            value={value}
                            onChange={setValue}
                            enabled={open}
                            idPrefix="mard"
                        />
                        <div className="mt-4 flex justify-end">
                            <Button
                                onClick={() => {
                                    assignMut.mutate();
                                }}
                                disabled={!canSubmit || assignMut.isPending}
                            >
                                Agregar rol
                            </Button>
                        </div>
                    </div>
                </Gate>

                <div className="mt-6 flex justify-end gap-2 border-t pt-4">
                    <Button
                        variant="outline"
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    >
                        Cerrar
                    </Button>
                </div>
            </div>
        </div>
    );
}
